/**
 * useUserPosts — paginated posts for one user's profile, one page state per tab.
 *
 * Lifts the pagination/caching machinery that lived inside the mobile profile
 * screen so both platforms share it. Wraps POST /Feeds/GetUserPosts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import feedService, { mapFeedApiResponse } from '../api/services/feedService';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import { normalizeUserGuid } from '../utils/navigateToUserProfile';
import type { ApiError } from '../types/api';
import type { FeedPost } from '../types/post';
import type { ProfileTabAction } from '../constants/profileTabs';

const PAGE_SIZE = 20;

export interface UserPostsTabState {
  posts: FeedPost[];
  page: number;
  hasNextPage: boolean;
  loading: boolean;
  isFetchingMore: boolean;
  error: string;
  /** False until the first response for this tab has been applied. */
  loaded: boolean;
}

const emptyTabState = (): UserPostsTabState => ({
  posts: [],
  page: 1,
  hasNextPage: false,
  loading: false,
  isFetchingMore: false,
  error: '',
  loaded: false,
});

export interface UseUserPostsResult extends UserPostsTabState {
  fetchNextPage: () => void;
  refresh: () => void;
  /** Optimistic like toggle, rolled back when the API rejects it. */
  likePost: (postId: string) => Promise<void>;
  /** Applies a partial change to one post across every cached tab. */
  patchPost: (postId: string, patch: Partial<FeedPost>) => void;
  /** Drops a post from every cached tab (optimistic delete). */
  removePost: (postId: string) => void;
}

/**
 * The API's `action` filter is coarse, so the result is narrowed again here —
 * `Photos` must not surface a text post that merely mentions an image.
 */
const matchesAction = (post: FeedPost, action: ProfileTabAction): boolean => {
  switch (action) {
    case 'Photos':
      return Boolean(post.image || (post.images && post.images.length > 0));
    case 'Videos':
      return Boolean(post.video);
    case 'Text':
      return !post.video && !post.image && !(post.images && post.images.length > 0);
    default:
      return true;
  }
};

interface UserPostsCache {
  /** Whose posts the tab states below belong to. */
  userId: number | null;
  tabs: Record<string, UserPostsTabState>;
}

/**
 * GetUserPosts omits the author GUID on some rows, which leaves the author
 * name, photo and "View Profile" inert. Every row except the Interacted tab
 * belongs to the profile being viewed, so its GUID fills the gap.
 */
const withAuthorGuid = (
  post: FeedPost,
  ownerUserId: number,
  ownerUserGuid: string | null | undefined,
): FeedPost => {
  if (normalizeUserGuid(post.profileUserGuid)) return post;
  const ownerGuid = normalizeUserGuid(ownerUserGuid);
  if (!ownerGuid || String(post.userId) !== String(ownerUserId)) return post;
  return { ...post, profileUserGuid: ownerGuid };
};

export const useUserPosts = (
  userId: number | null | undefined,
  action: ProfileTabAction | null,
  ownerUserGuid?: string | null,
): UseUserPostsResult => {
  const [cache, setCache] = useState<UserPostsCache>({ userId: null, tabs: {} });
  // Keyed `${userId}_${action}_${page}` — guards against duplicate in-flight
  // requests when a scroll handler and a tab switch fire together.
  const inFlight = useRef<Set<string>>(new Set());
  // Tracks the latest userId so stale responses for a previous profile
  // don't overwrite the cache when the user has already switched.
  const latestUserId = useRef(userId ?? null);
  latestUserId.current = userId ?? null;
  // Read through a ref so a late-arriving profile does not rebuild `load`.
  const ownerGuidRef = useRef(ownerUserGuid ?? null);
  ownerGuidRef.current = ownerUserGuid ?? null;

  const resolvedUserId = userId ?? null;
  // Reading a stale cache while the new user's first page loads would flash the
  // previous profile's posts, so a user change empties it during render.
  const tabs = useMemo(
    () => (cache.userId === resolvedUserId ? cache.tabs : {}),
    [cache, resolvedUserId],
  );
  const state = tabs[action ?? ''] ?? emptyTabState();

  const load = useCallback(
    async (targetAction: ProfileTabAction, page: number, replace: boolean) => {
      if (!userId) return;
      const requestKey = `${userId}_${targetAction}_${page}`;
      if (inFlight.current.has(requestKey)) return;
      inFlight.current.add(requestKey);

      const patchTab = (
        updater: (existing: UserPostsTabState) => UserPostsTabState,
      ): void => {
        setCache(current => {
          const base = current.userId === userId ? current.tabs : {};
          const existing = base[targetAction] ?? emptyTabState();
          return {
            userId,
            tabs: { ...base, [targetAction]: updater(existing) },
          };
        });
      };

      patchTab(existing => ({
        ...existing,
        error: '',
        loading: page === 1,
        isFetchingMore: page > 1,
      }));

      try {
        const response = await feedService.getUserPosts({
          userId,
          startIndex: page,
          pageSize: PAGE_SIZE,
          action: targetAction,
        });
        // Drop the response if the user has switched since the request was made.
        if (latestUserId.current !== userId) return;
        const raw = response?.data?.feeds ?? [];
        const mapped = raw
          .map(post => mapFeedApiResponse(post as never))
          .map(post => withAuthorGuid(post, userId, ownerGuidRef.current))
          .filter(post => matchesAction(post, targetAction));

        patchTab(existing => ({
          posts: replace || page === 1 ? mapped : [...existing.posts, ...mapped],
          page,
          hasNextPage: raw.length >= PAGE_SIZE,
          loading: false,
          isFetchingMore: false,
          error: '',
          loaded: true,
        }));
      } catch (error) {
        // `loaded` stays as it was: a tab whose first page failed has answered
        // nothing, so the screen must not present it as empty. The stored
        // `error` is what stops the auto-load effect from looping.
        patchTab(existing => ({
          ...existing,
          loading: false,
          isFetchingMore: false,
          error: getApiErrorMessage(error as ApiError),
        }));
      } finally {
        inFlight.current.delete(requestKey);
      }
    },
    [userId],
  );

  // A tab keeps whatever it already fetched; only a first visit hits the API.
  useEffect(() => {
    if (!resolvedUserId || !action) return;
    const existing = tabs[action];
    if (existing?.loaded || existing?.loading || existing?.error) return;
    load(action, 1, true);
  }, [resolvedUserId, action, tabs, load]);

  useEffect(() => {
    inFlight.current.clear();
  }, [resolvedUserId]);

  const fetchNextPage = useCallback(() => {
    if (!action) return;
    const existing = tabs[action];
    if (!existing || !existing.hasNextPage || existing.loading || existing.isFetchingMore) {
      return;
    }
    load(action, existing.page + 1, false);
  }, [action, tabs, load]);

  const refresh = useCallback(() => {
    if (!action) return;
    load(action, 1, true);
  }, [action, load]);

  const mapAllTabs = useCallback(
    (transform: (posts: FeedPost[]) => FeedPost[]) => {
      setCache(current => {
        const next: Record<string, UserPostsTabState> = {};
        Object.entries(current.tabs).forEach(([tabKey, tabState]) => {
          next[tabKey] = { ...tabState, posts: transform(tabState.posts) };
        });
        return { ...current, tabs: next };
      });
    },
    [],
  );

  const patchPost = useCallback(
    (postId: string, patch: Partial<FeedPost>) => {
      mapAllTabs(posts =>
        posts.map(post => (post.id === postId ? { ...post, ...patch } : post)),
      );
    },
    [mapAllTabs],
  );

  const removePost = useCallback(
    (postId: string) => {
      mapAllTabs(posts => posts.filter(post => post.id !== postId));
    },
    [mapAllTabs],
  );

  const likePost = useCallback(
    async (postId: string) => {
      let currentlyLiked = false;
      const toggle = (post: FeedPost): FeedPost => {
        currentlyLiked = Boolean(post.liked);
        const liked = !post.liked;
        return {
          ...post,
          liked,
          likes: liked ? (post.likes || 0) + 1 : Math.max(0, (post.likes || 0) - 1),
        };
      };
      const apply = () =>
        mapAllTabs(posts => posts.map(post => (post.id === postId ? toggle(post) : post)));

      apply();
      try {
        const result = await feedService.likePost(postId, { currentlyLiked });
        if (!result.success) apply();
      } catch {
        apply();
      }
    },
    [mapAllTabs],
  );

  return { ...state, fetchNextPage, refresh, likePost, patchPost, removePost };
};

export default useUserPosts;
